import { useDispatch, useSelector } from 'react-redux';
import { components, MenuListProps, MultiValueProps, OptionProps } from 'react-select';
import cx from 'classnames';

import chevronDown from 'assets/icons/arrow_light_down.svg';
import plusSvg from 'assets/icons/plus.svg';
import crossSvg from 'assets/icons/close.svg';
import { getIsReseller } from 'store/publisher/selectors';
import { Store } from 'components/common/types/Store.types';
import { updateIsAdditionalDataIncluded } from 'store/pages/contentManagement/creativesAdditionalData/reducer';
import { CreativeMetaOperation } from 'components/common/types/Creative.types';
import withCancelRequest from 'components/hocs/withCancelRequest';
import AsyncSelect from 'lib/AsyncSelect';
import MultipleSwitchBox from 'lib/MultipleSwitchBox';
import Button, { Color, Size } from 'lib/Button';
import { CircularLoadingSpinner, SpinnerSize } from 'lib/CircularLoadingSpinner/CircularLoadingSpinner';
import { FilterProductCategory } from 'components/common/FilterArea/Filters';
import DataList from 'lib/DataList';
import DataListProps from 'lib/DataList/DataList.types';
import { SingleFieldSet, MultipleFieldSet } from './FieldSet';
import MotionType from './MotionType';
import { CreativesMetaFormProps } from './CreativesMetaForm.types';
import AdditionalData from './AdditionalData';
import useCreativesMetaFormData from './useCreativesMetaFormData';

const CreativesMetaForm = ({
  formMetadata,
  formMetadataOperations,
  isDisabled,
  onFormChange,
  onFormOperationChange,
  cancelFunctions,
}: CreativesMetaFormProps): React.ReactElement => {
  const dispatch = useDispatch();
  const isAdditionalDataIncluded = useSelector(
    (state: Store) => state.pages.contentManagement.creativesAdditionalData.isAdditionalDataIncluded,
  );
  const isReseller = useSelector(getIsReseller);

  const {
    onLoadAgencyLookupData,
    onLoadSpecialistLookupData,
    onLoadProductFormatLookupData,
    onSelectHandler,
    onBrandSelect,
    onAdvertiserChange,
    onLoadBrandsData,
    onLoadAdvertisersData,
    filterLoadedDealIdLookupData,
    defaultSameDspDealOptions,
    isLoadingSelectedCreativesOptions,
    productCategoriesOptions,
    areAllSelectedCreativesWithSameDSP,
  } = useCreativesMetaFormData({ cancelFunctions, formMetadata, onFormChange });

  return (
    <div data-test-id="creatives-meta-form" className="mb-32">
      <h3 className="my-4 sub-header-lg text-neutral-950-opacity-80">Edit creatives</h3>
      <MotionType value={formMetadata.movement} onChange={onFormChange} isDisabled={isDisabled} />
      <SingleFieldSet label="Campaign ID/name or Deal line ID/name" id="dealId" isSingleFieldSet>
        <div className="space-y-2 async-select-indicators-top">
          <AsyncSelect
            dataTestId="campaignId-input"
            name="dealId"
            loadData={filterLoadedDealIdLookupData}
            defaultOptions={defaultSameDspDealOptions}
            selectedValue={formMetadata.dealId}
            placeholder="Search by name or ID..."
            onSelect={onSelectHandler}
            isDisabled={isDisabled || !areAllSelectedCreativesWithSameDSP}
            multiple
            closeMenuOnSelect={false}
            textKey="title"
            valueKey="id"
            customComponents={{
              MultiValue: ({ data, removeProps, selectProps }: MultiValueProps & { data: DataListProps }) => (
                <div className="mb-1 flex w-full items-center gap-1 rounded-md border border-neutral-950-opacity-20 bg-white py-1 pl-2 pr-1 shadow-inner-sm">
                  <div className="min-w-0 flex-1">
                    <DataList data={data} variant="chip" />
                  </div>
                  <components.MultiValueRemove data={data} innerProps={removeProps} selectProps={selectProps} />
                </div>
              ),
              Option: ({ data, ...props }: OptionProps & { data: DataListProps }) => (
                // eslint-disable-next-line react/jsx-props-no-spreading
                <components.Option data={data} {...props}>
                  <DataList data={data} variant="option" />
                </components.Option>
              ),
              MenuList: ({ children, ...props }: MenuListProps): React.ReactElement => (
                // eslint-disable-next-line react/jsx-props-no-spreading
                <components.MenuList {...props}>
                  {children}
                  {isLoadingSelectedCreativesOptions && (
                    <div className="flex justify-center py-6">
                      <CircularLoadingSpinner size={SpinnerSize.SMALL} />
                    </div>
                  )}
                </components.MenuList>
              ),
            }}
          />
          <div className="flex justify-start">
            <MultipleSwitchBox
              source={[
                { code: CreativeMetaOperation.ADD, name: 'Add', icon: plusSvg },
                { code: CreativeMetaOperation.REPLACE, name: 'Replace', icon: crossSvg },
              ]}
              selectedValue={formMetadataOperations.dealId}
              textKey="name"
              valueKey="code"
              onChange={onFormOperationChange('dealId')}
              isDisabled={isDisabled || !areAllSelectedCreativesWithSameDSP}
              hideBorder
              dataTestId="campaignId-select"
            />
          </div>
        </div>
      </SingleFieldSet>
      {!isReseller && (
        <>
          <MultipleFieldSet>
            <SingleFieldSet label="Advertiser" id="advertiser" isRequired>
              <AsyncSelect
                dataTestId="advertiser-input"
                name="advertiser"
                loadData={onLoadAdvertisersData}
                placeholder="Type here..."
                selectedValue={formMetadata.advertiser}
                onSelect={onAdvertiserChange}
                defaultOptions={formMetadata.brand?.advertisers || undefined}
                isDisabled={isDisabled}
              />
            </SingleFieldSet>
            <SingleFieldSet label="Brand" id="brand" isRequired>
              <AsyncSelect
                dataTestId="brands-input"
                name="brand"
                loadData={onLoadBrandsData}
                placeholder="Type here..."
                selectedValue={formMetadata.brand}
                onSelect={onBrandSelect}
                defaultOptions={formMetadata.advertiser?.brands || []}
                isDisabled={isDisabled}
              />
            </SingleFieldSet>
            <SingleFieldSet label="Product category" id="product-category">
              <FilterProductCategory
                dataTestId="product-category-select"
                options={productCategoriesOptions}
                selectedValue={formMetadata.productCategory}
                onSelect={onSelectHandler}
                isDisabled={isDisabled}
              />
            </SingleFieldSet>
          </MultipleFieldSet>
          <MultipleFieldSet>
            <SingleFieldSet label="Agency" id="agency">
              <AsyncSelect
                dataTestId="agency-input"
                name="agency"
                loadData={onLoadAgencyLookupData}
                placeholder="Type here..."
                selectedValue={formMetadata.agency}
                onSelect={onSelectHandler}
                isDisabled={isDisabled}
              />
            </SingleFieldSet>
            <SingleFieldSet label="Specialist" id="specialist">
              <AsyncSelect
                dataTestId="specialist-input"
                name="specialist"
                loadData={onLoadSpecialistLookupData}
                placeholder="Type here..."
                selectedValue={formMetadata.specialist}
                onSelect={onSelectHandler}
                isDisabled={isDisabled}
              />
            </SingleFieldSet>
            <SingleFieldSet label="Product format" id="productFormat">
              <div className="space-y-2">
                <AsyncSelect
                  dataTestId="product-format-input"
                  name="productFormat"
                  loadData={onLoadProductFormatLookupData}
                  selectedValue={formMetadata.productFormat}
                  placeholder="Select a product format"
                  onSelect={onSelectHandler}
                  isDisabled={isDisabled}
                  multiple
                />
                <div className="flex justify-start">
                  <MultipleSwitchBox
                    source={[
                      { code: CreativeMetaOperation.ADD, name: 'Add', icon: plusSvg },
                      { code: CreativeMetaOperation.REPLACE, name: 'Replace', icon: crossSvg },
                    ]}
                    selectedValue={formMetadataOperations.productFormat}
                    textKey="name"
                    valueKey="code"
                    onChange={onFormOperationChange('productFormat')}
                    isDisabled={isDisabled}
                    hideBorder
                    dataTestId="productFormat-select"
                  />
                </div>
              </div>
            </SingleFieldSet>
          </MultipleFieldSet>
        </>
      )}
      {isAdditionalDataIncluded && !isReseller && <AdditionalData />}
      {!isReseller && (
        <Button
          size={Size.WIDE}
          color={Color.BLACK_AND_WHITE}
          onClick={() => dispatch(updateIsAdditionalDataIncluded(!isAdditionalDataIncluded))}
          classNames="h-10 mt-4"
          svg={chevronDown}
          svgClassnames={cx('w-5', {
            'rotate-180': isAdditionalDataIncluded,
          })}
          label="Additional data"
        />
      )}
    </div>
  );
};

export default withCancelRequest(CreativesMetaForm);
